import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import logo from "../../assets/Main_Logo_AI_Colored_Padding.png";

// —— Design tokens (tweak if needed)
const COLORS = {
  green: "#14833b",
  greenDark: "#2e7d32",
  red: "#c62828",
  gray50: "#fafafa",
  gray100: "#f5f6f7",
  gray200: "#eef0f1",
  gray300: "#e5e7eb",
  text: "#111827",
  sub: "#6b7280",
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingHorizontal: 24,
    paddingBottom: 70, // room for footer
    fontFamily: "Helvetica",
    fontSize: 12,
  },

  // Header
  title: { fontSize: 22, fontWeight: 700, textAlign: "center", color: COLORS.green, marginBottom: 10 },
  subRow: { fontSize: 11, color: COLORS.sub, marginBottom: 16, textAlign: "center" },

  // Cards / sections
  card: {
    borderWidth: 1,
    borderColor: COLORS.gray200,
    backgroundColor: COLORS.gray100,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 12,
  },
  section: {
    borderWidth: 1,
    borderColor: COLORS.gray200,
    backgroundColor: "#fff",
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 12,
  },
  sectionTitle: { fontSize: 13, fontWeight: 700, marginBottom: 10, color: COLORS.text },

  // Pills / chips
  pillRow: { flexDirection: "row", gap: 10 },
  pill: {
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 10,
    fontSize: 11,
    fontWeight: 700,
    alignSelf: "flex-start",
  },
  gradePill: { backgroundColor: COLORS.greenDark, color: "#fff" },
  compliantPill: { backgroundColor: COLORS.greenDark, color: "#fff" },
  nonCompliantPill: { backgroundColor: COLORS.red, color: "#fff" },

  // Category chips
  chips: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  chip: {
    backgroundColor: COLORS.gray100,
    borderWidth: 1,
    borderColor: COLORS.gray200,
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 10,
    fontSize: 11,
    fontWeight: 700,
    color: COLORS.text,
  },

  // Lists
  bulletItem: { fontSize: 12, marginBottom: 6 },

  // Table
  table: {
    borderWidth: 1,
    borderColor: COLORS.gray300,
    borderRadius: 8,
    overflow: "hidden",
  },
  thead: {
    flexDirection: "row",
    backgroundColor: COLORS.gray100,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.gray300,
  },
  th: { flex: 1, fontSize: 11, fontWeight: 700, paddingVertical: 8, paddingHorizontal: 10 },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: COLORS.gray300,
  },
  td: { flex: 1, fontSize: 11, paddingVertical: 8, paddingHorizontal: 10, color: COLORS.text },

  // Footer
  footer: {
    position: "absolute",
    bottom: 16,
    left: 24,
    right: 24,
    height: 24,
    borderTopWidth: 1,
    borderTopColor: COLORS.gray300,
    paddingTop: 6,
    fontSize: 9,
    color: COLORS.sub,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  footerLeft: { fontSize: 9 },
  footerBrand: { flexDirection: "row", alignItems: "center" },
  footerLogo: { width: 60 },
});

const Footer = () => {
  const dateText = new Date().toLocaleDateString("en-US");
  return (
    <View style={styles.footer} fixed>
      <Text style={styles.footerLeft}>
        {`Generated by Portend AI • Confidential • ${dateText}`}
      </Text>
      <View style={styles.footerBrand}>
        {logo ? <Image style={styles.footerLogo} src={logo} /> : null}
      </View>
    </View>
  );
};

const CookieScanReportPDF = ({ data, pageTitle = "Cookie Scan Report" }) => {
  const categories = data?.result?.categories || {};
  const cookies = data?.result?.cookies || [];
  const grade = data?.grades?.finalGrade || "-";
  const compliant = !!data?.result?.complianceStatus?.compliant;
  const nonCompliantList = data?.result?.complianceStatus?.nonCompliantCookies || [];
  const reasons = data?.grades?.reason || [];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Title */}
        <Text style={styles.title}>{pageTitle}</Text>
        <Text style={styles.subRow}>Target: {data?.target || "-"}</Text>

        {/* Compliance & Grades (card with pills) */}
        <View style={styles.card}>
          <View style={styles.pillRow}>
            <Text style={[styles.pill, styles.gradePill]}>{`Final Grade: ${grade}`}</Text>
            <Text style={[styles.pill, compliant ? styles.compliantPill : styles.nonCompliantPill]}>
              {`Compliance: ${compliant ? "Compliant" : "Non-Compliant"}`}
            </Text>
          </View>
        </View>

        {/* Non-Compliant Cookies */}
        {!compliant && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Non-Compliant Cookies</Text>
            {nonCompliantList.length ? (
              nonCompliantList.map((c, i) => (
                <Text key={i} style={styles.bulletItem}> {c}</Text>
              ))
            ) : (
              <Text style={styles.bulletItem}>None</Text>
            )}
          </View>
        )}

        {/* Cookie Categories Breakdown (chips) */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Cookie Categories Breakdown</Text>
          <View style={styles.chips}>
            {Object.entries(categories).map(([k, v]) => (
              <Text key={k} style={styles.chip}>
                {`${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}`}
              </Text>
            ))}
          </View>
        </View>

        {/* Security Warnings & Recommendations */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Security Warnings & Recommendations</Text>
          {reasons.length ? (
            reasons.map((r, i) => (
              <Text key={i} style={styles.bulletItem}>⚠️ {r}</Text>
            ))
          ) : (
            <Text style={styles.bulletItem}>No significant warnings.</Text>
          )}
        </View>

        {/* Detailed Cookie Information (table) */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Detailed Cookie Information</Text>
          <View style={styles.table}>
            <View style={styles.thead}>
              <Text style={styles.th}>Name</Text>
              <Text style={styles.th}>Category</Text>
              <Text style={styles.th}>Expires</Text>
              <Text style={styles.th}>Vulnerabilities</Text>
              <Text style={styles.th}>Purpose</Text>
            </View>
            {cookies.map((c, i) => (
              <View key={i} style={styles.row}>
                <Text style={styles.td}>{c.name}</Text>
                <Text style={styles.td}>{c.category}</Text>
                <Text style={styles.td}>{c.expires}</Text>
                <Text style={styles.td}>{(c.vulnerabilities?.length ? c.vulnerabilities.join(", ") : "None")}</Text>
                <Text style={styles.td}>{c.purpose}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Footer */}
        <Footer />
      </Page>
    </Document>
  );
};

export default CookieScanReportPDF;
